import {Box, Text} from '@chakra-ui/react';
import React, {useEffect, useState} from 'react';
import Select, {PropsValue} from 'react-select';
import {useDebounce} from 'usehooks-ts';
import {useAccountTypeahead} from '../hooks/api/accountTypeahead';
import {useIsFreemiumCustomer} from '../hooks/api/metadata';
import {AccountWithLogo} from '../shared/scoredAccounts';
import {CompanyLogo} from './Icons/CompanyLogo';
import {
  getTypeaheadStyles,
  TypeaheadDropdownIndicator,
} from './utils/typeahead';

type OptionType = {value: AccountWithLogo};

export interface AccountTypeaheadProps {
  selectedAccount?: AccountWithLogo;
  onAccountSelect: (account: AccountWithLogo) => void;
  placeholder?: string;
  isDisabled?: boolean;
}

const formatOptionLabel = (
  data: {value: AccountWithLogo},
  {context}: {context: string}
) => (
  <Box display="flex" alignItems="center" ml="1" mt="0">
    <CompanyLogo
      domain={data.value.domain}
      name={data.value.name}
      boxSize="40px"
      mr="2"
    />
    {context === 'value' ? (
      <Text fontWeight="500" fontSize="20px">
        {data.value.name}
      </Text>
    ) : (
      <Text fontWeight="thin" fontSize="14px">
        {data.value.name}
      </Text>
    )}
  </Box>
);

export const SingleAccountTypeahead: React.FC<AccountTypeaheadProps> = ({
  selectedAccount,
  onAccountSelect,
  placeholder = 'Start typing to pick an account...',
  isDisabled,
}) => {
  const [search, setSearch] = useState('');
  const [displayedAccount, setDisplayedAccount] = useState<
    PropsValue<{value: AccountWithLogo}> | undefined
  >(selectedAccount ? {value: selectedAccount} : undefined);

  useEffect(() => {
    setDisplayedAccount(selectedAccount ? {value: selectedAccount} : undefined);
  }, [selectedAccount, setDisplayedAccount]);

  const debouncedSearch = useDebounce<string>(search, 200);
  const accountTypeaheadQuery = useAccountTypeahead(debouncedSearch);
  const isFreemium = useIsFreemiumCustomer();

  const typeaheadAccounts = accountTypeaheadQuery.data ?? [];

  return (
    <Select<OptionType, false>
      menuPortalTarget={document.body}
      placeholder={placeholder}
      value={displayedAccount}
      noOptionsMessage={() => null}
      styles={getTypeaheadStyles<OptionType, false>()}
      components={{
        DropdownIndicator: TypeaheadDropdownIndicator,
        IndicatorSeparator: null,
      }}
      formatOptionLabel={formatOptionLabel}
      inputValue={search}
      onInputChange={(val: string) => setSearch(val)}
      isMulti={false}
      isClearable={true}
      isDisabled={isDisabled !== undefined ? isDisabled : isFreemium}
      hideSelectedOptions={true}
      controlShouldRenderValue={true}
      options={typeaheadAccounts.map((account) => ({
        value: account,
        label: account.domain,
      }))}
      onChange={(selected) => {
        if (selected) {
          setDisplayedAccount(selected);
          onAccountSelect(selected.value);
        } else {
          setDisplayedAccount(undefined);
        }
      }}
      onBlur={() => {
        if (selectedAccount) {
          setDisplayedAccount({value: selectedAccount});
        }
      }}
    />
  );
};
